import { Box, Checkbox, Typography } from "@mui/material";
import pluralize from "pluralize";
import React, { useState, useEffect, FC } from "react";
import { cloneDeep, startCase } from "lodash";
import toast from "react-hot-toast";
import { AxiosResponse } from "axios";
import {
  getBrandEmbeds,
  addVideoToEmbed,
  removeVideoFromEmbed,
} from "../../../../api/endpoints";
import axiosInstant from "../../../../api";
import { IEmbed } from "../../embeds/embedsV2/interface";
import { DotLottleLoader } from "../../../../components/Loader";

interface IEmbedMap {
  embed: IEmbed;
  isSelected: boolean;
}

interface Props {
  search: string;
  videoId?: number | null;
  brandId: number;
  shouldSubmit?: boolean;
  onClose?: () => void;
  /** Fires only when at least one add/remove was submitted. */
  onComplete?: () => void;
}
export const EmbedSelectV2: FC<Props> = (props) => {
  const { videoId, brandId, shouldSubmit, onClose, onComplete, search } = props;

  const [loading, setLoading] = useState(false);
  const [prevEmbedsMap, setPrevEmbedsMap] = useState<Array<IEmbedMap>>([]);
  const [newEmbedsMap, setNewEmbedsMap] = useState<Array<IEmbedMap>>([]);

  const addVideo = (embed: IEmbed) => {
    const payload = { embedId: embed.id, fileId: videoId };
    return axiosInstant.post(addVideoToEmbed(), payload);
  };
  const removeVideo = (embed: IEmbed) =>
    axiosInstant.delete(removeVideoFromEmbed(embed.id, videoId!));

  const handleSubmit = async () => {
    const promises: Promise<AxiosResponse<any, any>>[] = [];
    newEmbedsMap.forEach(({ embed, isSelected }, index) => {
      if (isSelected !== prevEmbedsMap[index].isSelected) {
        if (isSelected) promises.push(addVideo(embed));
        else promises.push(removeVideo(embed));
      }
    });
    try {
      await Promise.all(promises);
    } catch (err) {
      toast((err as Error)?.message);
    }
    // Partial failures may still have applied changes — let the host refresh.
    // A save with no changes skips the refetch.
    if (promises.length) onComplete?.();
    onClose?.();
  };

  const handleSelectEmbed = (index: number, isSelected: boolean) => {
    const tempEmbeds = [...newEmbedsMap];
    tempEmbeds[index].isSelected = isSelected;
    setNewEmbedsMap([...tempEmbeds]);
  };

  const fetchEmbeds = async () => {
    setLoading(true);
    const { data } = await axiosInstant.get(
      getBrandEmbeds(brandId, 1000, 1, search)
    );
    setLoading(false);
    if (data.rows.length > 0) {
      const output = [...data.rows].map((e: IEmbed) => ({
        embed: e,
        isSelected: !!e.files.find((v) => v.id === videoId),
      }));
      setPrevEmbedsMap(cloneDeep(output));
      setNewEmbedsMap(cloneDeep(output));
    }
  };

  const addTag = (title: string, count = 0) => {
    if (!count) return null;
    return (
      <Box
        className="flex items-center justify-center p-1 px-2 bg-surface-2 !rounded-2xl text-text-2 text-xs"
        sx={{ fontFamily: "CircularStd" }}
      >
        {startCase(pluralize(title, count, true))}
      </Box>
    );
  };

  useEffect(() => {
    fetchEmbeds();
  }, [search]);

  useEffect(() => {
    if (shouldSubmit) handleSubmit();
  }, [shouldSubmit]);

  if (loading) return <DotLottleLoader />;
  return (
    <>
      {newEmbedsMap.map(({ isSelected, embed }, index) => (
        <Box
          className="w-full flex py-2 px-6 items-center justify-between cursor-pointer hover:bg-surface-2"
          key={embed.id}
          onClick={() => handleSelectEmbed(index, !isSelected)}
        >
          <Box className="flex gap-4 items-center">
            <Checkbox size="medium" checked={isSelected} className="!p-0" />
            <Typography className="text-text-1" variant="body1">
              {embed.name}
            </Typography>
          </Box>
          <Box className="gap-1 items-center hidden sm:flex">
            {addTag("products", 2)}
            {addTag("videos", embed.files?.length)}
          </Box>
        </Box>
      ))}
    </>
  );
};
